import { useState, useRef, useEffect } from "react";
import { router } from "@inertiajs/react";
import { twMerge } from "tailwind-merge";
import {
  ChevronUpDownIcon,
  CheckIcon,
  PlusIcon,
  EllipsisHorizontalIcon,
  Cog6ToothIcon,
  UserPlusIcon,
  ArrowRightStartOnRectangleIcon,
  RocketLaunchIcon,
} from "@heroicons/react/24/outline";
import { Popover, PopoverTrigger, PopoverContent } from "@components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@components/ui/dropdown-menu";
import {
  useCurrentWorkspace,
  useAccounts,
  type AccountWithWorkspaces,
  type AccountWorkspace,
} from "~/stores/sessionStore";
import {
  useSwitchWorkspace,
  useCreateWorkspace,
  useRenameWorkspace,
  useDeleteWorkspace,
} from "~/api/workspaces.hooks";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@components/ui/dialog";
import { InviteMembersModal } from "./InviteMembersModal";

interface WorkspaceSwitcherProps {
  isCollapsed: boolean;
  onToggleCollapse?: () => void;
}

function WorkspaceAvatar({ name, size = "sm" }: { name: string; size?: "sm" | "md" | "lg" }) {
  const letter = name.charAt(0).toUpperCase();
  const sizeClasses = size === "lg" ? "w-9 h-9 text-base" : size === "md" ? "w-8 h-8 text-sm" : "w-6 h-6 text-xs";
  return (
    <div
      className={twMerge(
        "rounded-md flex items-center justify-center font-semibold bg-gray-200 text-gray-700 shrink-0",
        sizeClasses
      )}
    >
      {letter}
    </div>
  );
}

function AccountHeader({
  account,
  onSettingsClick,
  onInviteClick,
  onNavigateHome,
}: {
  account: AccountWithWorkspaces;
  onSettingsClick: () => void;
  onInviteClick: () => void;
  onNavigateHome?: () => void;
}) {
  const planLabel = [account.plan_name, `${account.member_count} member${account.member_count !== 1 ? "s" : ""}`]
    .filter(Boolean)
    .join(" \u00b7 ");

  const identity = (
    <>
      <WorkspaceAvatar name={account.name} />
      <div className="flex-1 min-w-0 text-left">
        <p className="text-sm font-semibold truncate" data-testid="account-name">{account.name}</p>
        <p className="text-xs text-muted-foreground truncate" data-testid="account-meta">{planLabel}</p>
      </div>
    </>
  );

  return (
    <div className="px-3 pt-3 pb-2" data-testid={`account-section-${account.id}`}>
      {onNavigateHome ? (
        <button
          onClick={onNavigateHome}
          data-testid="account-home-button"
          className="flex items-center gap-2 w-full -mx-1 px-1 py-1 rounded-md hover:bg-accent transition-colors"
        >
          {identity}
        </button>
      ) : (
        <div className="flex items-center gap-2">{identity}</div>
      )}
      {account.is_current && account.is_admin && (
        <div className="flex gap-1.5 mt-2">
          <button
            onClick={onSettingsClick}
            data-testid="account-settings-button"
            className="flex items-center gap-1 px-2.5 py-1 text-xs rounded-md border border-gray-200 text-muted-foreground hover:bg-accent transition-colors"
          >
            <Cog6ToothIcon className="w-3.5 h-3.5" />
            Settings
          </button>
          <button
            onClick={onInviteClick}
            data-testid="invite-members-button"
            className="flex items-center gap-1 px-2.5 py-1 text-xs rounded-md border border-gray-200 text-muted-foreground hover:bg-accent transition-colors"
          >
            <UserPlusIcon className="w-3.5 h-3.5" />
            Invite members
          </button>
        </div>
      )}
    </div>
  );
}

function WorkspaceRow({
  workspace,
  isCurrent,
  isRenaming,
  renameValue,
  onRenameValueChange,
  onRenameConfirm,
  onRenameCancel,
  renameInputRef,
  renamePending,
  onSwitch,
  onStartRename,
  onDelete,
  canDelete,
  isAdmin,
}: {
  workspace: AccountWorkspace;
  isCurrent: boolean;
  isRenaming: boolean;
  renameValue: string;
  onRenameValueChange: (v: string) => void;
  onRenameConfirm: () => void;
  onRenameCancel: () => void;
  renameInputRef: React.RefObject<HTMLInputElement | null>;
  renamePending: boolean;
  onSwitch: () => void;
  onStartRename: () => void;
  onDelete: () => void;
  canDelete: boolean;
  isAdmin: boolean;
}) {
  if (isRenaming) {
    return (
      <div className="px-3 py-1">
        <input
          ref={renameInputRef}
          data-testid="workspace-rename-input"
          type="text"
          value={renameValue}
          onChange={(e) => onRenameValueChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") onRenameConfirm();
            if (e.key === "Escape") onRenameCancel();
          }}
          onBlur={() => {
            if (renameValue.trim() && renameValue.trim() !== workspace.name) {
              onRenameConfirm();
            } else {
              onRenameCancel();
            }
          }}
          className="w-full text-sm px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-primary"
          disabled={renamePending}
        />
      </div>
    );
  }

  return (
    <div className="group relative" data-testid={`workspace-row-${workspace.id}`}>
      <button
        onClick={onSwitch}
        data-testid={`workspace-switch-${workspace.id}`}
        className="flex items-center gap-2 w-full px-2 py-2 rounded-md text-sm hover:bg-accent transition-colors"
      >
        <WorkspaceAvatar name={workspace.name} />
        <span className="truncate flex-1 text-left" data-testid="workspace-name">{workspace.name}</span>
        {isCurrent && <CheckIcon className="w-4 h-4 text-primary shrink-0" data-testid="workspace-current-check" />}
        {isAdmin && <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              onClick={(e) => e.stopPropagation()}
              className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-gray-200 transition-opacity"
            >
              <EllipsisHorizontalIcon className="w-4 h-4 text-muted-foreground" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="right" align="start" className="border-gray-200/80 shadow-lg">
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                onStartRename();
              }}
            >
              Rename
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              disabled={!canDelete}
              className={!canDelete ? "text-muted-foreground" : "text-destructive focus:text-destructive"}
            >
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>}
      </button>
    </div>
  );
}

function NewWorkspaceInput({
  isCreating,
  setIsCreating,
  newName,
  setNewName,
  onCreate,
  isPending,
  createInputRef,
}: {
  isCreating: boolean;
  setIsCreating: (v: boolean) => void;
  newName: string;
  setNewName: (v: string) => void;
  onCreate: () => void;
  isPending: boolean;
  createInputRef: React.RefObject<HTMLInputElement | null>;
}) {
  if (isCreating) {
    return (
      <div className="px-3 py-1">
        <input
          ref={createInputRef}
          type="text"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") onCreate();
            if (e.key === "Escape") {
              setIsCreating(false);
              setNewName("");
            }
          }}
          onBlur={() => {
            if (newName.trim()) {
              onCreate();
            } else {
              setIsCreating(false);
            }
          }}
          placeholder={isPending ? "Creating..." : "Workspace name..."}
          className="w-full text-sm px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-primary"
          disabled={isPending}
        />
      </div>
    );
  }

  return (
    <button
      onClick={() => setIsCreating(true)}
      data-testid="new-workspace-button"
      className="flex items-center gap-2 w-full px-2 py-2 rounded-md text-sm text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
    >
      <PlusIcon className="w-4 h-4" />
      <span>New workspace</span>
    </button>
  );
}

export function WorkspaceSwitcher({ isCollapsed, onToggleCollapse }: WorkspaceSwitcherProps) {
  const currentWorkspace = useCurrentWorkspace();
  const accounts = useAccounts();
  const switchWorkspace = useSwitchWorkspace();
  const createWorkspace = useCreateWorkspace();
  const renameWorkspace = useRenameWorkspace();
  const deleteWorkspace = useDeleteWorkspace();

  const [open, setOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [renamingId, setRenamingId] = useState<number | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: number; name: string } | null>(null);
  const createInputRef = useRef<HTMLInputElement>(null);
  const renameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isCreating && createInputRef.current) {
      createInputRef.current.focus();
    }
  }, [isCreating]);

  useEffect(() => {
    if (renamingId && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [renamingId]);

  const handleCreate = () => {
    if (!newName.trim()) return;
    createWorkspace.mutate(newName.trim(), {
      onSuccess: () => {
        setNewName("");
        setIsCreating(false);
        setOpen(false);
      },
    });
  };

  const handleRename = (id: number) => {
    if (!renameValue.trim()) return;
    renameWorkspace.mutate(
      { id, name: renameValue.trim() },
      { onSuccess: () => setRenamingId(null) }
    );
  };

  const handleDelete = (id: number) => {
    deleteWorkspace.mutate(id);
  };

  const navigateHome = () => {
    setOpen(false);
    router.visit("/");
  };

  const handleSwitch = (id: number) => {
    setOpen(false);
    // Clicking the workspace you're already in still takes you home rather
    // than no-opping — the switcher doubles as a "back to home" affordance.
    if (id === currentWorkspace?.id) {
      router.visit("/");
      return;
    }
    switchWorkspace.mutate(id);
  };

  const handleLogout = () => {
    setOpen(false);
    const csrfToken = document.querySelector<HTMLMetaElement>("meta[name='csrf-token']")?.content;
    fetch("/users/sign_out", {
      method: "DELETE",
      headers: {
        "X-CSRF-Token": csrfToken || "",
        Accept: "application/json",
      },
      credentials: "same-origin",
    }).finally(() => {
      window.location.replace("/users/sign_in");
    });
  };

  const displayName = currentWorkspace?.name || "Workspace";
  const singleAccount = accounts.length <= 1;

  // Backend sorts: current first, then subscribed, then alphabetical
  const sortedAccounts = accounts;

  const handleUpgrade = (accountId: number) => {
    setOpen(false);
    // Switch to the unsubscribed account, which will redirect to pricing
    fetch(`/accounts/${accountId}/switch`, {
      method: "PATCH",
      headers: {
        "X-CSRF-Token": document.querySelector<HTMLMetaElement>("meta[name='csrf-token']")?.content || "",
        Accept: "text/html",
      },
      credentials: "same-origin",
    }).then(() => {
      window.location.href = "/pricing";
    });
  };

  const renderAccountContent = (account: AccountWithWorkspaces) => {
    if (!account.subscribed) {
      return (
        <button
          onClick={() => handleUpgrade(account.id)}
          data-testid={`account-upgrade-${account.id}`}
          className="flex items-center gap-2 w-full px-2 py-2 rounded-md text-sm text-primary-600 hover:bg-accent cursor-pointer transition-colors"
        >
          <RocketLaunchIcon className="w-4 h-4" />
          <span>Subscribe to start building</span>
        </button>
      );
    }

    return (
      <>
        {account.workspaces.map((workspace) => (
          <WorkspaceRow
            key={workspace.id}
            workspace={workspace}
            isCurrent={workspace.is_current}
            isRenaming={renamingId === workspace.id}
            renameValue={renameValue}
            onRenameValueChange={setRenameValue}
            onRenameConfirm={() => handleRename(workspace.id)}
            onRenameCancel={() => setRenamingId(null)}
            renameInputRef={renameInputRef}
            renamePending={renameWorkspace.isPending}
            onSwitch={() => handleSwitch(workspace.id)}
            onStartRename={() => {
              setRenamingId(workspace.id);
              setRenameValue(workspace.name);
            }}
            onDelete={() => setDeleteConfirm({ id: workspace.id, name: workspace.name })}
            canDelete={account.workspaces.length > 1}
            isAdmin={account.is_admin}
          />
        ))}
        {account.is_admin && (
          <NewWorkspaceInput
            isCreating={isCreating}
            setIsCreating={setIsCreating}
            newName={newName}
            setNewName={setNewName}
            onCreate={handleCreate}
            isPending={createWorkspace.isPending}
            createInputRef={createInputRef}
          />
        )}
      </>
    );
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <div className="flex items-center">
          <PopoverTrigger asChild>
            <button
              data-testid="workspace-switcher-trigger"
              className={twMerge(
                "flex items-center gap-2.5 text-left text-white hover:bg-white/10 transition-colors",
                isCollapsed ? "justify-center p-3 w-full" : "px-4 py-4 flex-1 min-w-0",
                switchWorkspace.isPending && "opacity-60 pointer-events-none"
              )}
            >
              <WorkspaceAvatar name={displayName} size={isCollapsed ? "md" : "lg"} />
              {!isCollapsed && (
                <>
                  <span className="text-base font-semibold truncate flex-1">{displayName}</span>
                  {switchWorkspace.isPending ? (
                    <svg className="w-5 h-5 shrink-0 animate-spin text-white/60" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  ) : (
                    <ChevronUpDownIcon className="w-5 h-5 shrink-0 opacity-60" />
                  )}
                </>
              )}
            </button>
          </PopoverTrigger>
          {!isCollapsed && onToggleCollapse && (
            <button
              onClick={onToggleCollapse}
              className="text-white hover:opacity-70 transition-opacity pr-3 shrink-0"
            >
              <ArrowRightStartOnRectangleIcon className="w-6 h-6 rotate-180" />
            </button>
          )}
        </div>

        <PopoverContent
          side="bottom"
          align="start"
          sideOffset={8}
          alignOffset={12}
          className="w-72 p-0 max-h-[70vh] overflow-y-auto rounded-xl shadow-lg border-gray-200/80"
          data-testid="workspace-switcher-popover"
        >
          {singleAccount ? (
            /* Single account: no headers, just workspaces */
            <div className="py-1">
              {accounts[0] && (
                <>
                  <AccountHeader
                    account={accounts[0]}
                    onNavigateHome={accounts[0].is_current ? navigateHome : undefined}
                    onSettingsClick={() => {
                      setOpen(false);
                      router.visit("/settings");
                    }}
                    onInviteClick={() => {
                      setOpen(false);
                      setInviteOpen(true);
                    }}
                  />
                  <div className="py-1 px-1">
                    {renderAccountContent(accounts[0])}
                  </div>
                </>
              )}
            </div>
          ) : (
            /* Multi-account: grouped sections */
            <div>
              {sortedAccounts.map((account, idx) => (
                <div key={account.id}>
                  {idx > 0 && <div className="h-px bg-border" />}
                  <AccountHeader
                    account={account}
                    onNavigateHome={account.is_current ? navigateHome : undefined}
                    onSettingsClick={() => {
                      setOpen(false);
                      router.visit("/settings");
                    }}
                    onInviteClick={() => {
                      setOpen(false);
                      setInviteOpen(true);
                    }}
                  />
                  <div className="py-1 px-1">
                    {renderAccountContent(account)}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Divider + Log out */}
          <div className="h-px bg-border" />
          <div className="py-1 px-1">
            <button
              onClick={handleLogout}
              data-testid="workspace-switcher-logout"
              className="flex items-center gap-2 w-full px-2 py-2 rounded-md text-sm text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
            >
              <ArrowRightStartOnRectangleIcon className="w-4 h-4" />
              <span>Log out</span>
            </button>
          </div>
        </PopoverContent>
      </Popover>

      <InviteMembersModal open={inviteOpen} onOpenChange={setInviteOpen} />

      {/* Delete workspace confirmation */}
      <Dialog open={!!deleteConfirm} onOpenChange={(v) => !v && setDeleteConfirm(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete workspace</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{deleteConfirm?.name}</strong>? All projects in this workspace will be moved to the default workspace.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 justify-end mt-2">
            <button
              onClick={() => setDeleteConfirm(null)}
              className="px-3 py-1.5 text-sm rounded-md border border-gray-200 hover:bg-accent transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                if (deleteConfirm) {
                  handleDelete(deleteConfirm.id);
                  setDeleteConfirm(null);
                }
              }}
              className="px-3 py-1.5 text-sm rounded-md bg-destructive text-white hover:bg-destructive/90 transition-colors"
            >
              Delete
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
